/**
 * VisitTemplateForm - LCD-Compliant Visit Documentation with Speech-to-Text
 * 
 * This component provides discipline-specific visit templates with:
 * - Body systems assessments based on LCD requirements
 * - Speech-to-text enabled text areas
 * - Real-time QA validation
 * - Prompts for clinicians on which assessments to complete
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "./ui/alert";
import { ScrollArea } from "./ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Mic,
  MicOff,
  AlertTriangle,
  CheckCircle,
  Info,
  FileText,
  Heart,
  Brain,
  Activity,
  Stethoscope,
  Save,
  Send,
  RefreshCw,
  HelpCircle,
  ChevronRight,
  AlertCircle,
  Clipboard,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import SpeechDictation from "./SpeechDictation";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Body system icons
const SYSTEM_ICONS = {
  cardiovascular: Heart,
  respiratory: Activity,
  neurological: Brain,
  gastrointestinal: Stethoscope,
  genitourinary: Stethoscope,
  musculoskeletal: Activity,
  integumentary: Stethoscope,
  pain: AlertCircle,
  mental_status: Brain,
  nutritional: Stethoscope,
  functional_status: Activity,
  terminal_prognosis: FileText,
};

export default function VisitTemplateForm({
  patientId,
  patientName,
  clinicianId,
  onSave,
  onCancel,
  initialDiscipline = null,
  initialVisitType = null,
}) {
  const [disciplines, setDisciplines] = useState([]);
  const [selectedDiscipline, setSelectedDiscipline] = useState(initialDiscipline || "");
  const [selectedVisitType, setSelectedVisitType] = useState(initialVisitType || "");
  const [template, setTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [validationResult, setValidationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPrompts, setShowPrompts] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [expandedSystems, setExpandedSystems] = useState([]);
  const [narrative, setNarrative] = useState("");

  // Fetch disciplines on mount
  useEffect(() => {
    const fetchDisciplines = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/visit-templates/disciplines`);
        setDisciplines(res.data.disciplines || []);
      } catch (err) {
        console.error("Failed to fetch disciplines:", err);
        toast.error("Failed to load disciplines");
      }
    };
    fetchDisciplines();
  }, []);

  // Fetch template when discipline and visit type are selected
  useEffect(() => {
    if (selectedDiscipline && selectedVisitType) {
      fetchTemplate();
    }
  }, [selectedDiscipline, selectedVisitType]);

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/visit-templates/template/${selectedDiscipline}/${selectedVisitType}`,
        { params: { patient_id: patientId } }
      );
      setTemplate(res.data);
      
      // Initialize form data structure
      const initialData = {};
      res.data.body_systems.forEach((system) => {
        initialData[system.id] = {};
      });
      setFormData(initialData);
      
      // Auto-expand required systems
      const requiredSystems = res.data.body_systems
        .filter((s) => s.required)
        .map((s) => s.id);
      setExpandedSystems(requiredSystems);
      
      // Fetch prompts
      fetchPrompts();
    } catch (err) {
      console.error("Failed to fetch template:", err);
      toast.error(err.response?.data?.detail || "Failed to load template");
    } finally {
      setLoading(false);
    }
  };

  const fetchPrompts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/visit-templates/prompts/${selectedDiscipline}`);
      setPrompts(res.data.prompts || []);
    } catch (err) {
      console.error("Failed to fetch prompts:", err);
    }
  };

  // Update form data for a specific field
  const updateField = useCallback((systemId, fieldId, value) => {
    setFormData((prev) => ({
      ...prev,
      [systemId]: {
        ...prev[systemId],
        [fieldId]: value,
      },
    }));
  }, []);

  // Handle speech transcript for a field
  const handleSpeechTranscript = useCallback((systemId, fieldId, transcript) => {
    setFormData((prev) => {
      const currentValue = prev[systemId]?.[fieldId] || "";
      const newValue = currentValue
        ? (currentValue.endsWith(" ") || currentValue.endsWith("\n")
          ? currentValue + transcript
          : currentValue + " " + transcript)
        : transcript;
      return {
        ...prev,
        [systemId]: {
          ...prev[systemId],
          [fieldId]: newValue,
        },
      };
    });
  }, []);

  // Validate documentation
  const validateDocumentation = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/visit-templates/validate`, {
        patient_id: patientId,
        discipline: selectedDiscipline,
        visit_type: selectedVisitType,
        visit_date: new Date().toISOString(),
        body_systems: formData,
        narrative: narrative,
        clinician_id: clinicianId,
      });
      setValidationResult(res.data);
      return res.data;
    } catch (err) {
      console.error("Validation failed:", err);
      toast.error("Validation failed");
      return null;
    }
  };

  // Save documentation
  const handleSave = async () => {
    setSaving(true);
    
    // Validate first
    const validation = await validateDocumentation();
    
    if (!validation) {
      setSaving(false);
      return;
    }
    
    if (!validation.is_valid) {
      toast.error("Please resolve critical issues before saving");
      setSaving(false);
      return;
    }
    
    try {
      const res = await axios.post(`${API_URL}/api/visit-templates/save`, {
        patient_id: patientId,
        discipline: selectedDiscipline,
        visit_type: selectedVisitType,
        visit_date: new Date().toISOString(),
        body_systems: formData,
        narrative: narrative,
        clinician_id: clinicianId,
      });
      
      toast.success("Visit documentation saved successfully!");
      if (onSave) {
        onSave(res.data);
      }
    } catch (err) {
      console.error("Save failed:", err);
      toast.error(err.response?.data?.detail?.message || "Failed to save documentation");
    } finally {
      setSaving(false);
    }
  };

  // Calculate completion percentage
  const calculateCompletion = () => {
    if (!template) return 0;
    
    let totalRequired = 0;
    let completed = 0;
    
    template.body_systems.forEach((system) => {
      if (system.required) {
        system.fields.forEach((field) => {
          if (field.required && !field.conditional) {
            totalRequired++;
            if (formData[system.id]?.[field.id]) {
              completed++;
            }
          }
        });
      }
    });
    
    return totalRequired > 0 ? Math.round((completed / totalRequired) * 100) : 0;
  };

  // Render a field based on its type
  const renderField = (systemId, field) => {
    const value = formData[systemId]?.[field.id] || "";
    
    // Check conditional visibility
    if (field.conditional) {
      const conditionValue = formData[systemId]?.[field.conditional];
      if (!conditionValue) return null;
    }
    
    switch (field.type) {
      case "number":
        return (
          <div key={field.id} className="space-y-1">
            <Label htmlFor={`${systemId}-${field.id}`} className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
              {field.unit && <span className="text-muted-foreground ml-1">({field.unit})</span>}
            </Label>
            <Input
              id={`${systemId}-${field.id}`}
              type="number"
              min={field.min}
              max={field.max}
              value={value}
              onChange={(e) => updateField(systemId, field.id, parseFloat(e.target.value))}
              className="w-32"
              data-testid={`field-${systemId}-${field.id}`}
            />
          </div>
        );
      
      case "boolean":
        return (
          <div key={field.id} className="flex items-center justify-between py-2">
            <Label htmlFor={`${systemId}-${field.id}`} className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Switch
              id={`${systemId}-${field.id}`}
              checked={value === true}
              onCheckedChange={(checked) => updateField(systemId, field.id, checked)}
              data-testid={`field-${systemId}-${field.id}`}
            />
          </div>
        );
      
      case "select":
        return (
          <div key={field.id} className="space-y-1">
            <Label htmlFor={`${systemId}-${field.id}`} className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Select
              value={value}
              onValueChange={(val) => updateField(systemId, field.id, val)}
            >
              <SelectTrigger data-testid={`field-${systemId}-${field.id}`}>
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {field.options.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      
      case "multiselect":
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div key={field.id} className="space-y-1">
            <Label className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="flex flex-wrap gap-2">
              {field.options.map((opt) => (
                <Badge
                  key={opt}
                  variant={selectedValues.includes(opt) ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => {
                    const newValues = selectedValues.includes(opt)
                      ? selectedValues.filter((v) => v !== opt)
                      : [...selectedValues, opt];
                    updateField(systemId, field.id, newValues);
                  }}
                  data-testid={`field-${systemId}-${field.id}-${opt}`}
                >
                  {opt}
                </Badge>
              ))}
            </div>
          </div>
        );
      
      case "textarea":
        return (
          <div key={field.id} className="space-y-1">
            <div className="flex items-center justify-between">
              <Label htmlFor={`${systemId}-${field.id}`} className="text-sm">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </Label>
              {field.speech_enabled && (
                <SpeechDictation
                  onTranscript={(text) => handleSpeechTranscript(systemId, field.id, text)}
                  buttonVariant="ghost"
                  buttonSize="sm"
                />
              )}
            </div>
            <Textarea
              id={`${systemId}-${field.id}`}
              value={value}
              onChange={(e) => updateField(systemId, field.id, e.target.value)}
              placeholder={field.speech_enabled ? "Type or use speech dictation..." : "Enter notes..."}
              rows={3}
              data-testid={`field-${systemId}-${field.id}`}
            />
          </div>
        );
      
      case "text":
        return (
          <div key={field.id} className="space-y-1">
            <Label htmlFor={`${systemId}-${field.id}`} className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={`${systemId}-${field.id}`}
              type="text"
              value={value}
              onChange={(e) => updateField(systemId, field.id, e.target.value)}
              data-testid={`field-${systemId}-${field.id}`}
            />
          </div>
        );
      
      case "date":
        return (
          <div key={field.id} className="space-y-1">
            <Label htmlFor={`${systemId}-${field.id}`} className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={`${systemId}-${field.id}`}
              type="date"
              value={value}
              onChange={(e) => updateField(systemId, field.id, e.target.value)}
              data-testid={`field-${systemId}-${field.id}`}
            />
          </div>
        );
      
      default:
        return null;
    }
  };

  // Get available visit types for selected discipline
  const getVisitTypes = () => {
    const disc = disciplines.find((d) => d.id === selectedDiscipline);
    return disc?.visit_types || [];
  };

  const completion = calculateCompletion();

  return (
    <div className="space-y-4">
      {/* Discipline & Visit Type Selection */}
      {!template && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clipboard className="w-5 h-5" />
              Select Visit Template
            </CardTitle>
            <CardDescription>
              Choose your discipline and visit type to load the appropriate template
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Discipline</Label>
                <Select value={selectedDiscipline} onValueChange={setSelectedDiscipline}>
                  <SelectTrigger data-testid="discipline-select">
                    <SelectValue placeholder="Select discipline..." />
                  </SelectTrigger>
                  <SelectContent>
                    {disciplines.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Visit Type</Label>
                <Select
                  value={selectedVisitType}
                  onValueChange={setSelectedVisitType}
                  disabled={!selectedDiscipline}
                >
                  <SelectTrigger data-testid="visit-type-select">
                    <SelectValue placeholder="Select visit type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {getVisitTypes().map((vt) => (
                      <SelectItem key={vt} value={vt}>
                        {vt.charAt(0).toUpperCase() + vt.slice(1)} Visit
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {patientName && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Patient</AlertTitle>
                <AlertDescription>{patientName}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {loading && (
        <Card>
          <CardContent className="py-8 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-primary" />
            <p>Loading template...</p>
          </CardContent>
        </Card>
      )}

      {/* Template Form */}
      {template && !loading && (
        <>
          {/* Header with Progress */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">
                    {template.discipline_name} - {template.visit_type.charAt(0).toUpperCase() + template.visit_type.slice(1)} Visit
                  </CardTitle>
                  <CardDescription>
                    {patientName && `Patient: ${patientName}`}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPrompts(true)}
                  >
                    <HelpCircle className="w-4 h-4 mr-1" />
                    Guidance
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={validateDocumentation}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Validate
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Completion: {completion}%</span>
                  <span className="text-muted-foreground">
                    {template.body_systems.filter((s) => s.required).length} required systems
                  </span>
                </div>
                <Progress value={completion} className="h-2" />
              </div>
            </CardContent>
          </Card>

          {/* Validation Results */}
          {validationResult && (
            <Card className={validationResult.is_valid ? "border-green-500" : "border-red-500"}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  {validationResult.is_valid ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      Documentation Valid
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      Issues Found
                    </>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {validationResult.critical_issues.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-red-600">Critical Issues:</p>
                    {validationResult.critical_issues.map((issue, idx) => (
                      <Alert key={idx} variant="destructive" className="py-2">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{issue.message}</AlertDescription>
                      </Alert>
                    ))}
                  </div>
                )}
                {validationResult.warnings.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-yellow-600">Warnings:</p>
                    {validationResult.warnings.map((warning, idx) => (
                      <Alert key={idx} className="py-2 border-yellow-500">
                        <AlertTriangle className="h-4 w-4 text-yellow-500" />
                        <AlertDescription>{warning.message}</AlertDescription>
                      </Alert>
                    ))}
                  </div>
                )}
                {validationResult.lcd_indicators_found.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-blue-600">LCD Indicators Found:</p>
                    {validationResult.lcd_indicators_found.map((indicator, idx) => (
                      <Badge key={idx} variant="secondary" className="mr-1 mb-1">
                        {indicator}
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-sm text-muted-foreground">
                  Completeness Score: {validationResult.completeness_score}%
                </p>
              </CardContent>
            </Card>
          )}

          {/* Body Systems Accordion */}
          <Accordion
            type="multiple"
            value={expandedSystems}
            onValueChange={setExpandedSystems}
            className="space-y-2"
          >
            {template.body_systems.map((system) => {
              const Icon = SYSTEM_ICONS[system.id] || Stethoscope;
              const systemComplete = system.fields
                .filter((f) => f.required && !f.conditional)
                .every((f) => formData[system.id]?.[f.id]);
              
              return (
                <AccordionItem
                  key={system.id}
                  value={system.id}
                  className="border rounded-lg"
                >
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${system.required ? "bg-primary/10" : "bg-slate-100"}`}>
                        <Icon className={`w-5 h-5 ${system.required ? "text-primary" : "text-slate-500"}`} />
                      </div>
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{system.name}</span>
                          {system.required && (
                            <Badge variant="outline" className="text-xs">Required</Badge>
                          )}
                          {systemComplete && (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {system.fields.length} fields • {system.lcd_indicators.length} LCD indicators
                        </p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-4">
                      {/* LCD Indicators Reference */}
                      {system.lcd_indicators.length > 0 && (
                        <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                          <p className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                            LCD Indicators to Document:
                          </p>
                          <ul className="text-xs text-blue-600 dark:text-blue-400 list-disc list-inside">
                            {system.lcd_indicators.map((indicator, idx) => (
                              <li key={idx}>{indicator}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Fields */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {system.fields.map((field) => renderField(system.id, field))}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>

          {/* Narrative Section */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Visit Narrative</CardTitle>
                <SpeechDictation
                  onTranscript={(text) => {
                    setNarrative((prev) =>
                      prev ? (prev.endsWith(" ") ? prev + text : prev + " " + text) : text
                    );
                  }}
                  buttonVariant="outline"
                  buttonSize="sm"
                />
              </div>
            </CardHeader>
            <CardContent>
              <Textarea
                value={narrative}
                onChange={(e) => setNarrative(e.target.value)}
                placeholder="Document your overall visit findings, patient response, and plan of care..."
                rows={6}
                data-testid="visit-narrative"
              />
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={validateDocumentation}>
                <CheckCircle className="w-4 h-4 mr-1" />
                Validate
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-1" />
                    Save Documentation
                  </>
                )}
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Prompts Dialog */}
      <Dialog open={showPrompts} onOpenChange={setShowPrompts}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Documentation Guidance</DialogTitle>
            <DialogDescription>
              Prompts to help you complete the assessment correctly
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {prompts.map((prompt, idx) => (
              <div
                key={idx}
                className={`p-4 rounded-lg border ${
                  prompt.required ? "border-primary bg-primary/5" : "border-slate-200"
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium">{prompt.name}</span>
                  {prompt.required && (
                    <Badge variant="default">Required</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{prompt.prompt}</p>
                {prompt.when_to_use && (
                  <ul className="mt-2 text-xs text-muted-foreground list-disc list-inside">
                    {prompt.when_to_use.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                )}
                {prompt.lcd_indicators.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-medium">LCD Indicators:</p>
                    <ul className="text-xs text-blue-600 list-disc list-inside">
                      {prompt.lcd_indicators.slice(0, 3).map((ind, i) => (
                        <li key={i}>{ind}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowPrompts(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
